import { Text } from '@components/primitives';
import { Toast, useToastContext } from '@contexts/ToastContext';
import { AntDesign } from '@expo/vector-icons';
import colors from '@theme/colors';
import { scale } from '@utils/normalize';
import React from 'react';
import { View, StyleSheet, TouchableOpacity, Animated, Platform } from 'react-native';

interface ToastItemProps {
  toast: Toast;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast }) => {
  const { hide } = useToastContext();
  const [fadeAnim] = React.useState(new Animated.Value(0));
  const [slideAnim] = React.useState(new Animated.Value(-100));

  React.useEffect(() => {
    // Animate in
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  }, [fadeAnim, slideAnim]);

  const handleClose = () => {
    // Animate out
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: -100,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      hide(toast.id);
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return colors.success.green;
      case 'error':
        return colors.error.red;
      case 'warning':
        return colors.warning.orange;
      case 'info':
      default:
        return colors.primary.carnation;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return 'checkcircleo';
      case 'error':
        return 'closecircleo';
      case 'warning':
        return 'exclamationcircleo';
      case 'info':
      default:
        return 'infocirlceo';
    }
  };

  // If custom render function is provided, use it
  if (toast.render) {
    return (
      <Animated.View
        style={{
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        }}>
        {toast.render()}
      </Animated.View>
    );
  }

  return (
    <Animated.View
      style={[
        styles.container,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
          borderLeftColor: getStatusColor(toast.status || 'info'),
        },
      ]}>
      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <AntDesign
            name={getStatusIcon(toast.status || 'info')}
            size={scale(20)}
            color={getStatusColor(toast.status || 'info')}
          />
        </View>

        <View style={styles.textContainer}>
          {toast.title && (
            <Text variant="sm-bold" color={colors.neutral.charcoal} style={styles.title}>
              {toast.title}
            </Text>
          )}
          {toast.description && (
            <Text variant="xs-normal" color={colors.neutral.charcoal} style={styles.description}>
              {toast.description}
            </Text>
          )}
        </View>

        {toast.isClosable && (
          <TouchableOpacity
            onPress={handleClose}
            style={styles.closeButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <AntDesign name="close" size={scale(16)} color={colors.neutral.charcoal} />
          </TouchableOpacity>
        )}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  closeButton: {
    marginTop: scale(-2),
    padding: scale(4),
  },
  container: {
    backgroundColor: colors.neutral.cotton,
    borderLeftWidth: scale(4),
    borderRadius: scale(8),
    elevation: 3,
    shadowColor: colors.neutral.charcoal,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    }),
  },
  content: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    padding: scale(12),
  },
  description: {
    lineHeight: scale(16),
  },
  iconContainer: {
    marginRight: scale(8),
    marginTop: scale(2),
  },
  textContainer: {
    flex: 1,
    marginRight: scale(8),
  },
  title: {
    marginBottom: scale(2),
  },
});

export default ToastItem;
